/**
 * Datadog RUM Initialization
 *
 * Initializes Datadog Real User Monitoring (RUM) for client-side
 * error tracking, performance monitoring, and session replay.
 *
 * Healthcare-appropriate defaults:
 * - Session replay uses privacy: "mask" to prevent PHI capture
 * - beforeSend filters out potentially sensitive data from error messages
 * - Only enabled in staging/production (controlled by env var)
 *
 * Required environment variables:
 *   - VITE_DATADOG_APPLICATION_ID - RUM application ID from Datadog
 *   - VITE_DATADOG_CLIENT_TOKEN - Client token (safe for browser exposure)
 *
 * Optional environment variables:
 *   - VITE_DATADOG_SITE - Datadog site (defaults to datadoghq.com)
 *   - VITE_DATADOG_ENV - Environment name (defaults to "development")
 *   - VITE_DATADOG_SESSION_SAMPLE_RATE - % of sessions to track (defaults to 100)
 *   - VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE - % of sessions with replay (defaults to 20)
 */

import type { ErrorInfo } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin, addReactError } from "@datadog/browser-rum-react";
import {
  DomainEventNames,
  globalEventEmitter,
  type UserErrorShownEvent,
} from "@benchmark/core/events";
import {
  extractEventContext,
  sanitizeErrorContext,
  sanitizeTelemetryMessage,
  sanitizeTelemetryUrl,
} from "@/app/boot/telemetry";

/**
 * Check whether an error is an AbortError (request cancelled by user navigation
 * or component unmount). These are expected, non-actionable, and should not be
 * tracked in Datadog.
 * @agentic connections
 */
const isAbortError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  if (error instanceof Error) {
    if (error.name === "AbortError") return true;
    if (error.message.includes("AbortError")) return true;
    if (error.message.includes("The user aborted a request")) return true;
  }
  return false;
};

const APPLICATION_ID = import.meta.env["VITE_DATADOG_APPLICATION_ID"] as
  string | undefined;
const CLIENT_TOKEN = import.meta.env["VITE_DATADOG_CLIENT_TOKEN"] as
  string | undefined;
const SITE =
  (import.meta.env["VITE_DATADOG_SITE"] as string) || "datadoghq.com";
const ENV = (import.meta.env["VITE_DATADOG_ENV"] as string) || "development";
const VERSION = (import.meta.env["VITE_DATADOG_VERSION"] as string) || "1.0.0";
const SESSION_SAMPLE_RATE = Number(
  import.meta.env["VITE_DATADOG_SESSION_SAMPLE_RATE"] ?? "100",
);
const SESSION_REPLAY_SAMPLE_RATE = Number(
  import.meta.env["VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE"] ?? "20",
);

/**
 * Initialize Datadog RUM. Skips when credentials are missing (e.g. local development).
 * @agentic connections
 */
export const initializeDatadogRum = (): void => {
  if (!APPLICATION_ID || !CLIENT_TOKEN) {
    console.info(
      "[datadog-rum] Disabled: missing VITE_DATADOG_APPLICATION_ID or VITE_DATADOG_CLIENT_TOKEN",
    );
    return;
  }

  datadogRum.init({
    applicationId: APPLICATION_ID,
    clientToken: CLIENT_TOKEN,
    site: SITE,
    service: "connections",
    env: ENV,
    version: VERSION,

    // React integration: per-route RUM views (see routerConfig.tsx, which must
    // use `createBrowserRouter` from `@datadog/browser-rum-react/react-router-v7`)
    // and React component-stack error reporting via `addReactError`.
    plugins: [reactPlugin({ router: true })],

    // Session tracking
    sessionSampleRate: SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: SESSION_REPLAY_SAMPLE_RATE,

    // Feature flags
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,

    // Healthcare privacy: mask all user input in session replays
    defaultPrivacyLevel: "mask",

    // Healthcare privacy: mask auto-collected click action names too. With
    // `trackUserInteractions`, the SDK derives an action's name from the clicked
    // element's text/attributes — which in a clinical UI can be a patient name,
    // MRN, or other PHI. Pinning this to `true` makes `defaultPrivacyLevel`
    // apply to action-name computation so any element without an explicit,
    // reviewed `data-dd-action-name` becomes "Masked Element". Pinned
    // explicitly (not left to the SDK default) for the same reason as
    // `defaultPrivacyLevel` — SDK defaults have drifted across major versions.
    enablePrivacyForActionName: true,

    // Connect frontend traces to backend APM (dd-trace in apps/api).
    // Anchor each pattern to the start of the URL and to a `/` or end-of-
    // string after the hostname so an attacker can't sneak the trusted
    // hostname into a path or query of an arbitrary origin (CodeQL
    // js/regex/missing-regexp-anchor).
    allowedTracingUrls: [
      /^https:\/\/[a-z0-9-]+\.benchmarkhealth\.com(?:\/|$)/,
      /^https:\/\/api\.medplum\.com(?:\/|$)/,
    ],

    // Filter noise and potentially sensitive data before sending to Datadog
    beforeSend: (event) => {
      // Drop AbortErrors — these are expected when users navigate away
      // mid-request and are not actionable (e.g. "Error parsing response 200 AbortError")
      if (event.type === "error") {
        const message = event.error?.message ?? "";
        if (
          message.includes("AbortError") ||
          message.includes("The user aborted a request")
        ) {
          return false;
        }
      }

      // Mask FHIR resource identifiers (PHI) and strip credential params from
      // every URL leaving the browser. RUM attaches the raw browser URL to
      // *all* event types (view, action, error, resource) and our routes embed
      // full patient/resource UUIDs in the path (`/Patient/<uuid>/timeline`)
      // and `*Id` query params. The view's `name`/`url_path_group` stay
      // route-templated for analytics; only the raw ids are removed.
      if (event.view?.url) {
        event.view.url = sanitizeTelemetryUrl(event.view.url);
      }
      if (event.view?.referrer) {
        event.view.referrer = sanitizeTelemetryUrl(event.view.referrer);
      }
      if (event.type === "resource" && event.resource?.url) {
        event.resource.url = sanitizeTelemetryUrl(event.resource.url);
      }
      // Error events raised from a failed request carry the request URL on
      // `error.resource.url` (e.g. a 4xx/5xx Medplum FHIR search with a patient
      // reference or name term) — sanitize it on the same footing. Optional
      // chaining throughout so a malformed event can never abort beforeSend.
      if (event.type === "error" && event.error?.resource?.url) {
        event.error.resource.url = sanitizeTelemetryUrl(
          event.error.resource.url,
        );
      }
      // The error message/stack are untrusted upstream strings that can embed
      // PHI (a Medplum error interpolating a patient reference, MRN, phone,
      // email, or DOB). URL masking above does not cover the message body, so
      // scrub both. Runs after the AbortError drop above.
      if (event.type === "error" && event.error) {
        if (typeof event.error.message === "string") {
          event.error.message = sanitizeTelemetryMessage(event.error.message);
        }
        if (typeof event.error.stack === "string") {
          event.error.stack = sanitizeTelemetryMessage(event.error.stack);
        }
      }
      return true;
    },
  });

  console.info(
    `[datadog-rum] Initialized: service=connections, env=${ENV}, site=${SITE}`,
  );
};

/**
 * Set the current user context on Datadog RUM. Call after authentication.
 * @agentic connections
 */
export const setDatadogUser = (user: {
  id: string;
  name?: string;
  email?: string;
}): void => {
  if (!APPLICATION_ID || !CLIENT_TOKEN) return;

  datadogRum.setUser({
    id: user.id,
    name: user.name,
    email: user.email,
  });
};

/**
 * Clear the user context (e.g. on logout).
 * @agentic connections
 */
export const clearDatadogUser = (): void => {
  if (!APPLICATION_ID || !CLIENT_TOKEN) return;

  datadogRum.clearUser();
};

/** Runs regardless of Datadog credentials so the AuditEvent path works in every env. */
const publishUserError = (source: string, error: unknown): void =>
  globalEventEmitter.publish<UserErrorShownEvent>(
    DomainEventNames.UserErrorShown,
    { source, severity: "serious", error },
  );

/**
 * Manually report an error to Datadog RUM and publish `user-error.shown`.
 * Use for caught errors that should still be tracked.
 * @agentic connections
 */
export const reportError = (
  error: unknown,
  context?: Record<string, unknown>,
): void => {
  if (isAbortError(error)) return;
  publishUserError(
    typeof context?.["source"] === "string"
      ? context["source"]
      : "report-error",
    error,
  );
  if (!APPLICATION_ID || !CLIENT_TOKEN) return;

  // Sanitize caller context before it leaves the app: primitives only,
  // masked identifiers, capped strings (see telemetry.ts / PHI rules).
  datadogRum.addError(
    error,
    context ? sanitizeErrorContext(context) : undefined,
  );
};

/**
 * Report a React render error (from an error boundary's `onError`) to Datadog
 * with the component stack attached. Credential-guarded and AbortError-filtered
 * like {@link reportError}. Requires the React plugin (registered in `init`).
 * @agentic connections
 */
export const reportReactError = (error: unknown, info: ErrorInfo): void => {
  if (isAbortError(error)) return;
  publishUserError("error-boundary", error);
  if (!APPLICATION_ID || !CLIENT_TOKEN) return;

  addReactError(
    error instanceof Error ? error : new Error(String(error)),
    info,
  );
};

/**
 * Connect the global event emitter to Datadog RUM for error forwarding and custom actions.
 * @agentic connections
 */
export const connectEventEmitterToDatadog = (emitter: {
  onError: (
    handler: (error: unknown, eventName: string, payload: unknown) => void,
  ) => void;
  onPublish: (handler: (eventName: string, payload: unknown) => void) => void;
}): void => {
  if (!APPLICATION_ID || !CLIENT_TOKEN) return;

  emitter.onError((error, eventName, _payload) => {
    if (isAbortError(error)) return;

    datadogRum.addError(error, {
      source: "event-emitter",
      eventName,
    });
  });

  emitter.onPublish((eventName, payload) => {
    // Forward a PHI-safe, allow-listed slice of the payload: identifiers
    // (`*Id`), allow-listed codes/statuses, and numeric/boolean measures.
    // Full FHIR resources are objects and are dropped; free-text strings are
    // dropped unless the key is an identifier. See `telemetry.ts`.
    datadogRum.addAction(eventName, extractEventContext(payload));
  });
};
